/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as TextCopyActionCreators from '@app/actions/TextCopyActionCreators';
import * as UserActionCreators from '@app/actions/UserActionCreators';
import styles from '@app/components/phone/PhoneSnaHandoffPanel.module.css';
import {Button} from '@app/components/uikit/button/Button';
import {QRCodeCanvas} from '@app/components/uikit/QRCodeCanvas';
import {Spinner} from '@app/components/uikit/Spinner';
import {Platform} from '@app/lib/Platform';
import {isPwaOnMobileOrTablet} from '@app/utils/PwaUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {CheckCircleIcon, ClipboardIcon, DeviceMobileIcon, WarningCircleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useMemo, useState} from 'react';

interface PhoneSnaHandoffPanelProps {
	handoff: UserActionCreators.PhoneSnaHandoffSendResponse;
	onComplete: (phoneToken: string) => Promise<void> | void;
	onFallbackSms?: () => Promise<void> | void;
	onBack?: () => void;
}

type PanelStatus = 'waiting' | 'checking' | 'completed' | 'expired' | 'failed';

export const PhoneSnaHandoffPanel = observer(function PhoneSnaHandoffPanel({
	handoff,
	onComplete,
	onFallbackSms,
	onBack,
}: PhoneSnaHandoffPanelProps) {
	const {i18n} = useLingui();
	const canAttemptInlineSna = useMemo(() => Platform.isMobileBrowser || isPwaOnMobileOrTablet(), []);
	const [status, setStatus] = useState<PanelStatus>(canAttemptInlineSna ? 'checking' : 'waiting');
	const [isFallingBack, setIsFallingBack] = useState(false);

	useEffect(() => {
		let isCancelled = false;

		const completeFromPoll = async () => {
			const {phone_token} = await UserActionCreators.waitForPhoneSnaHandoffToken(
				handoff.handoff_id,
				handoff.expires_at,
			);
			if (isCancelled) return;
			setStatus('completed');
			await onComplete(phone_token);
		};

		const run = async () => {
			try {
				if (canAttemptInlineSna) {
					setStatus('checking');
					const mobileResult = await UserActionCreators.completePhoneSnaHandoffOnMobile(
						handoff.handoff_id,
						handoff.token,
					);
					if (isCancelled) return;
					if (mobileResult.status === 'expired') {
						setStatus('expired');
						return;
					}
				}
				await completeFromPoll();
			} catch {
				if (!isCancelled) {
					setStatus(Date.now() > new Date(handoff.expires_at).getTime() ? 'expired' : 'failed');
				}
			}
		};

		void run();

		return () => {
			isCancelled = true;
		};
	}, [canAttemptInlineSna, handoff.expires_at, handoff.handoff_id, handoff.token, onComplete]);

	const handleFallback = async () => {
		if (!onFallbackSms) return;
		setIsFallingBack(true);
		try {
			await UserActionCreators.cancelPhoneSnaHandoff(handoff.handoff_id).catch(() => {});
			await onFallbackSms();
		} finally {
			setIsFallingBack(false);
		}
	};

	const statusContent = (() => {
		switch (status) {
			case 'checking':
				return (
					<div className={styles.statusRow}>
						<Spinner size="small" />
						<span>
							<Trans>Checking your mobile connection...</Trans>
						</span>
					</div>
				);
			case 'completed':
				return (
					<div className={clsx(styles.statusRow, styles.statusSuccess)}>
						<CheckCircleIcon size={18} weight="fill" />
						<span>
							<Trans>Phone verified.</Trans>
						</span>
					</div>
				);
			case 'expired':
				return (
					<div className={clsx(styles.statusRow, styles.statusError)}>
						<WarningCircleIcon size={18} weight="fill" />
						<span>
							<Trans>This verification link expired.</Trans>
						</span>
					</div>
				);
			case 'failed':
				return (
					<div className={clsx(styles.statusRow, styles.statusError)}>
						<WarningCircleIcon size={18} weight="fill" />
						<span>
							<Trans>Automatic verification did not complete.</Trans>
						</span>
					</div>
				);
			default:
				return (
					<div className={styles.statusRow}>
						<Spinner size="small" />
						<span>
							<Trans>Waiting for your phone...</Trans>
						</span>
					</div>
				);
		}
	})();

	return (
		<div className={styles.panel}>
			<div className={styles.intro}>
				<DeviceMobileIcon className={styles.icon} size={24} weight="fill" />
				<div>
					<h3 className={styles.title}>
						<Trans>Verify with your mobile network</Trans>
					</h3>
					<p className={styles.description}>
						{canAttemptInlineSna ? (
							<Trans>
								Keep cellular data enabled while we check this phone. You can also scan the QR code with another phone.
							</Trans>
						) : (
							<Trans>Scan this QR code with your phone and keep cellular data enabled.</Trans>
						)}
					</p>
				</div>
			</div>

			<div className={styles.qrSection}>
				<QRCodeCanvas data={handoff.mobile_url} size={164} />
				{statusContent}
			</div>

			{canAttemptInlineSna && (
				<div className={styles.mobileActions}>
					<div className={styles.buttonGroup}>
						<Button
							variant="secondary"
							onClick={() => TextCopyActionCreators.copy(i18n, handoff.mobile_url)}
							leftIcon={<ClipboardIcon size={16} weight="bold" />}
						>
							<Trans>Copy Link</Trans>
						</Button>
					</div>
				</div>
			)}

			<div className={styles.buttonGroup}>
				{!handoff.required && onFallbackSms && (
					<Button variant="secondary" onClick={handleFallback} submitting={isFallingBack}>
						<Trans>Use SMS Instead</Trans>
					</Button>
				)}
				{onBack && (
					<Button variant="secondary" onClick={onBack} disabled={isFallingBack}>
						<Trans>Back</Trans>
					</Button>
				)}
			</div>
		</div>
	);
});
